/**
 * Google Analytics 4 Service
 * Centralized analytics tracking for Fat & Slim app
 * 
 * Measurement ID: G-M40H9E7X7D
 */

// Ensure gtag is available
const gtag = (...args) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
};

/**
 * Track page view
 * @param {string} pagePath - The page path (e.g., '/dashboard')
 * @param {string} pageTitle - The page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: window.location.href
  });
};

/**
 * Track user login
 * @param {string} method - Login method ('email', 'google', etc.)
 */
export const trackLogin = (method = 'email') => {
  gtag('event', 'login', {
    method: method
  });
};

/**
 * Track user sign up
 * @param {string} method - Signup method ('email', 'google', etc.)
 */
export const trackSignUp = (method = 'email') => {
  gtag('event', 'sign_up', {
    method: method
  });
};

/**
 * Track food logged
 * @param {string} foodName - Name of the food
 * @param {number} calories - Calories of the food
 * @param {string} mealType - Type of meal (breakfast, lunch, dinner, snack)
 */
export const trackFoodLogged = (foodName, calories, mealType) => {
  gtag('event', 'food_logged', {
    food_name: foodName,
    calories: calories,
    meal_type: mealType,
    event_category: 'nutrition'
  });
};

/**
 * Track workout completed
 * @param {string} workoutName - Name of the workout
 * @param {number} durationMinutes - Duration in minutes
 * @param {number} caloriesBurned - Estimated calories burned
 * @param {string} category - Workout category
 */
export const trackWorkoutCompleted = (workoutName, durationMinutes, caloriesBurned, category) => {
  gtag('event', 'workout_completed', {
    workout_name: workoutName,
    duration_minutes: durationMinutes,
    calories_burned: caloriesBurned,
    workout_category: category,
    event_category: 'fitness'
  });
};

/**
 * Track workout video started
 * @param {string} videoId - YouTube video ID
 * @param {string} videoTitle - Video title
 * @param {string} category - Video category
 */
export const trackWorkoutVideoStarted = (videoId, videoTitle, category) => {
  gtag('event', 'video_start', {
    video_id: videoId,
    video_title: videoTitle,
    video_category: category,
    event_category: 'fitness'
  });
};

/**
 * Track recipe viewed
 * @param {string} recipeName - Name of the recipe
 * @param {string} category - Recipe category
 */
export const trackRecipeViewed = (recipeName, category) => {
  gtag('event', 'recipe_viewed', {
    recipe_name: recipeName,
    recipe_category: category,
    event_category: 'nutrition'
  });
};

/**
 * Track onboarding completed
 * @param {object} userData - User profile data
 */
export const trackOnboardingCompleted = (userData) => {
  gtag('event', 'onboarding_completed', {
    user_goal: userData?.goal,
    fitness_level: userData?.fitness_level,
    event_category: 'engagement'
  });
};

/**
 * Track premium subscription started
 * @param {string} planType - Subscription plan type
 * @param {number} value - Subscription value
 */
export const trackPremiumSubscription = (planType, value) => {
  gtag('event', 'purchase', {
    transaction_id: `premium_${Date.now()}`,
    value: value,
    currency: 'EUR',
    items: [{
      item_id: planType,
      item_name: 'Fat & Slim Premium',
      category: 'subscription',
      price: value
    }]
  });
};

/**
 * Track AI chat interaction
 * @param {string} chatType - Type of chat ('coach', 'recipe_generator', etc.)
 */
export const trackAIChatInteraction = (chatType) => {
  gtag('event', 'ai_chat_interaction', {
    chat_type: chatType,
    event_category: 'ai_features'
  });
};

/**
 * Track weight logged
 * @param {number} weight - Weight in kg
 */
export const trackWeightLogged = (weight) => {
  gtag('event', 'weight_logged', {
    weight_kg: weight,
    event_category: 'health_tracking'
  });
};

/**
 * Track water intake
 * @param {number} glasses - Number of glasses
 */
export const trackWaterIntake = (glasses) => {
  gtag('event', 'water_logged', {
    glasses: glasses,
    event_category: 'health_tracking'
  });
};

/**
 * Track steps logged
 * @param {number} steps - Number of steps
 */
export const trackStepsLogged = (steps) => {
  gtag('event', 'steps_logged', {
    steps: steps,
    event_category: 'health_tracking'
  });
};

/**
 * Track share action
 * @param {string} contentType - Type of content shared
 * @param {string} method - Share method
 */
export const trackShare = (contentType, method) => {
  gtag('event', 'share', {
    content_type: contentType,
    method: method
  });
};

/**
 * Track screen/page navigation
 * @param {string} screenName - Name of the screen
 */
export const trackScreenView = (screenName) => {
  gtag('event', 'screen_view', {
    screen_name: screenName
  });
};

/**
 * Track button click
 * @param {string} buttonName - Name/identifier of the button
 * @param {string} location - Where the button is located
 */
export const trackButtonClick = (buttonName, location) => {
  gtag('event', 'button_click', {
    button_name: buttonName,
    button_location: location,
    event_category: 'engagement'
  });
};

/**
 * Track error occurrence
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 */
export const trackError = (errorType, errorMessage) => {
  gtag('event', 'exception', {
    description: `${errorType}: ${errorMessage}`,
    fatal: false
  });
};

/**
 * Set user ID for tracking (after login)
 * @param {string} userId - User's unique ID
 */
export const setUserId = (userId) => {
  gtag('config', 'G-M40H9E7X7D', {
    user_id: userId
  });
};

/**
 * Set user properties
 * @param {object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  gtag('set', 'user_properties', properties);
};

export default {
  trackPageView,
  trackLogin,
  trackSignUp,
  trackFoodLogged,
  trackWorkoutCompleted,
  trackWorkoutVideoStarted,
  trackRecipeViewed,
  trackOnboardingCompleted,
  trackPremiumSubscription,
  trackAIChatInteraction,
  trackWeightLogged,
  trackWaterIntake,
  trackStepsLogged,
  trackShare,
  trackScreenView,
  trackButtonClick,
  trackError,
  setUserId,
  setUserProperties
};
